import { IUser } from "../Auth"
import { useXHR } from "@sembark-travel/xhr"
import { dateToUTCString, endOf, startOf } from "@sembark-travel/datetime-utils"
import useSWR from "swr"
import { ITrip } from "../Trips"
import { type TWATemplate } from "../Integrations"

export type TCommentableType =
	| "trips"
	| "instalments"
	| "tenants"
	| "trip_plan_requests"

/**
 * A WhatsApp template a task carries, sent to the record's customer when
 * the task falls due and nobody has resolved it.
 */
export interface ICommentWAReminder {
	id: number
	status: "pending" | "sending" | "sent" | "skipped" | "failed"
	send_at: string
	sent_at?: string
	recipient?: string
	template: { id?: number; name?: string; language?: string }
	phone_number: { id?: number; display_phone_number?: string }
	wa_thread_id?: number
	wa_message_id?: number
	/** Only ever set when the status is `failed`. */
	failure_reason?: string
}

export interface IComment {
	id: number
	body: string
	due_at?: string
	resolved_at?: string
	resolved_by?: IUser
	is_resolved: boolean
	needs_resolution: boolean
	assigned_to?: IUser
	created_by: IUser
	created_at: string
	commentable_id: number
	commentable_type: TCommentableType
	contact?: {
		name: string
		phone_number: string
	}
	trip?: ITrip
	wa_reminder?: ICommentWAReminder
}

/**
 * Which kind of record fills a template's variables, per kind of task.
 *
 * Mirrors `App\CommentWAReminder::TARGETS` on the server, which refuses a
 * template bound to anything else. `tenants` is absent on purpose: a note
 * about the agency itself has no customer to message.
 */
const WHATSAPP_SOURCE_KEYS: Partial<Record<TCommentableType, string>> = {
	trips: "trip",
	instalments: "trip_instalment",
	trip_plan_requests: "trip_plan_request",
}

export function whatsAppSourceKeyFor(
	commentableType: TCommentableType
): string | undefined {
	return WHATSAPP_SOURCE_KEYS[commentableType]
}

export type TWhatsAppReminderValues = {
	notify_customer_on_whatsapp?: boolean
	wa_template?: TWATemplate
	wa_phone_number?: { id: number }
}

/**
 * Turn the form's own fields into what the comment endpoints accept. The
 * switch and the picked objects never go to the server -- only the two ids
 * do, and only when the switch is on.
 */
export function whatsAppReminderPayload(values: TWhatsAppReminderValues): {
	wa_message_template_id?: number
	wa_phone_number_id?: number
} {
	if (!values.notify_customer_on_whatsapp || !values.wa_template) {
		return {}
	}

	return {
		wa_message_template_id: values.wa_template.id,
		wa_phone_number_id: values.wa_phone_number?.id,
	}
}

export function useDemandingCommentsCount(from: Date, to: Date) {
	const xhr = useXHR()
	const status = "demanding"
	const due_after = dateToUTCString(startOf(from, "day"))
	const due_before = dateToUTCString(endOf(to, "day"))
	const { data } = useSWR(
		`comments-count?from=${due_after}&to=${due_before}`,
		() =>
			xhr
				.get<{ data: number }>("/comments-count", {
					params: {
						statuses: [status],
						due_after,
						due_before,
					},
				})
				.then((resp) => resp.data)
	)
	return { data }
}
